import type { DashboardData } from '@/hooks/dashboardDataTypes'

export type TimeTeamMember = {
  id: string
  name: string
  deals: number
  revenue: number
  goal: number
  achieved: number
  pipelineCount: number
  pipelineValue: number
}

export type TimeTabView = {
  totalClosers: number
  closedDeals: number
  closedRevenue: number
  pipelineTotalValue: number
  pipelineTotalCount: number
  monthlyGoal: number
  monthlyAchieved: number
  goalPercent: number
  avgTicket: number
  teamMembers: TimeTeamMember[]
  pipelineByCloser: { id: string; name: string; count: number; value: number }[]
}

export function emptyTimeTabView(): TimeTabView {
  return {
    totalClosers: 0,
    closedDeals: 0,
    closedRevenue: 0,
    pipelineTotalValue: 0,
    pipelineTotalCount: 0,
    monthlyGoal: 0,
    monthlyAchieved: 0,
    goalPercent: 0,
    avgTicket: 0,
    teamMembers: [],
    pipelineByCloser: [],
  }
}

export function mapTimeTabView(data: DashboardData): TimeTabView | null {
  if (!data.admin) return null

  const pipelineByCloser = data.admin.pipelineByCloser.map((row, index) => ({
    id: row.name || String(index),
    name: row.name,
    count: row.count,
    value: row.value,
  }))
  const pipelineMap = new Map(
    pipelineByCloser.map((row) => [row.name, { count: row.count, value: row.value }]),
  )

  const teamMembers: TimeTeamMember[] = data.admin.teamMembers.map((member) => {
    const pipeline = pipelineMap.get(member.name) ?? { count: 0, value: 0 }
    return {
      id: member.id,
      name: member.name,
      deals: member.deals,
      revenue: member.revenue,
      goal: member.goal,
      achieved: member.achieved,
      pipelineCount: pipeline.count,
      pipelineValue: pipeline.value,
    }
  })

  const closedDeals = data.admin.dealsPerMonth ?? 0
  const closedRevenue = data.admin.monthlyAchieved ?? 0
  const monthlyGoal = data.admin.monthlyGoal ?? 0
  const goalPercent = monthlyGoal > 0 ? (closedRevenue / monthlyGoal) * 100 : 0
  const avgTicket = closedDeals > 0 ? closedRevenue / closedDeals : 0

  return {
    totalClosers: data.admin.totalClosers,
    closedDeals,
    closedRevenue,
    pipelineTotalValue: pipelineByCloser.reduce((sum, row) => sum + row.value, 0),
    pipelineTotalCount: pipelineByCloser.reduce((sum, row) => sum + row.count, 0),
    monthlyGoal,
    monthlyAchieved: closedRevenue,
    goalPercent,
    avgTicket,
    teamMembers,
    pipelineByCloser,
  }
}

export function sortTeamByRevenue(members: TimeTeamMember[]): TimeTeamMember[] {
  return [...members].sort((a, b) => b.revenue - a.revenue || b.pipelineValue - a.pipelineValue)
}

export function formatGoalPercent(goalPercent: number, valuesVisible: boolean) {
  if (!valuesVisible) return '•••'
  return `${Math.min(goalPercent, 999).toFixed(1)}%`
}

export function buildTimeSalesChartData(view: TimeTabView) {
  return sortTeamByRevenue(view.teamMembers)
    .filter((row) => row.revenue > 0 || row.deals > 0)
    .map((row) => ({
      name: row.name,
      count: row.deals,
      value: row.revenue,
    }))
}

/** @deprecated Prefer buildTimeSalesChartData — mantido para compat de imports. */
export function buildTimePipelineChartData(view: TimeTabView) {
  return buildTimeSalesChartData(view)
}

export function hasTimeSalesData(rows: { value: number; count: number }[]): boolean {
  return rows.some((row) => row.value > 0 || row.count > 0)
}

export function hasTimePipelineData(rows: { value: number; count: number }[]): boolean {
  return hasTimeSalesData(rows)
}
